import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Box, MenuItem, Select } from '@mui/material'
import { ui } from '../../utils/uiTokens'
import { directorApi } from '../../features/director/directorApi'
import { useDirectorSubject, useHeadScope } from '../../pages/head/headScope'
import { dateRange } from '../../utils/periodDates'

// Те же диапазоны, что в EditCourseTopicScheduleDialog: другими темы не размечают
export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const WEEKS = [1, 2, 3, 4]

interface Props {
    monthNumber?: number
    weekNumber?: number
    onChange: (monthNumber?: number, weekNumber?: number) => void
    // передан (хотя бы null) - ай/апта ставятся сами по календарю периодов выбранного лека
    courseId?: number | null
    // ментор: пән выбранной группы и сама группа - при смене группы ай/апта ставятся заново
    courseSubjectId?: number
    groupId?: number
}

// лек+пән+топ, для которых уже поставили текущий период: дальше выбор пользователя не трогаем
const autoApplied = { key: '' }

const styles = {
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
    },
    select: {
        minWidth: 150,
        borderRadius: ui.radius,
        fontSize: '13.5px',
        fontWeight: 600,
        color: ui.ink,
        backgroundColor: ui.card,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    },
    weeks: {
        display: 'flex',
        gap: 0.75,
        flexWrap: 'wrap',
    },
    weekTab: (active: boolean) => ({
        padding: '8px 14px',
        borderRadius: ui.radius,
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: '13.5px',
        lineHeight: 1.2,
        fontWeight: active ? 600 : 400,
        color: active ? ui.pink : ui.sub,
        backgroundColor: active ? ui.pinkSoft : 'transparent',
        border: `1px solid ${active ? 'transparent' : ui.border}`,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': { borderColor: ui.pink, color: ui.pink },
    }),
}

const MonthWeekFilter: React.FC<Props> = ({
    monthNumber,
    weekNumber,
    onChange,
    courseId,
    courseSubjectId,
    groupId,
}) => {
    const { formatMessage } = useIntl()
    const { subjectId } = useDirectorSubject()
    const { director, readOnly } = useHeadScope()
    // страница группы и карточка ученика: курса нет, период берётся по пәну или группе
    const standalone = courseId === undefined && (!!courseSubjectId || !!groupId)
    const autoEnabled = courseId !== undefined || standalone
    const { data: current } = directorApi.useGetCurrentPeriodQuery(
        {
            courseId: courseId ?? 0,
            subjectId: subjectId ?? 0,
            courseSubjectId: courseSubjectId ?? 0,
            groupId: standalone && !courseSubjectId ? groupId : 0,
        },
        // ментору ждём пән группы, иначе успеет встать период чужого пәна
        { skip: !standalone && (!courseId || (!readOnly && !courseSubjectId)) },
    )
    const curMonth = current?.monthNumber || 0
    const curWeek = current?.weekNumber || 0
    const key = `${courseId}:${subjectId ?? 0}:${courseSubjectId ?? 0}:${groupId ?? 0}`

    useEffect(() => {
        if ((!courseId && !standalone) || !curMonth || autoApplied.key === key) return
        autoApplied.key = key
        if (monthNumber !== curMonth || (weekNumber ?? 0) !== curWeek) onChange(curMonth, curWeek || undefined)
    }, [key, curMonth, curWeek])

    const onToday = monthNumber === curMonth && (weekNumber ?? 0) === curWeek

    return (
        <Box sx={styles.wrapper}>
            <Select
                size="small"
                displayEmpty
                value={monthNumber ?? ''}
                onChange={(e) =>
                    onChange(Number(e.target.value) || undefined, weekNumber)
                }
                sx={styles.select}
            >
                <MenuItem value="">
                    {formatMessage({ id: 'allMonths' })}
                </MenuItem>
                {MONTHS.map((month) => (
                    <MenuItem key={month} value={month}>
                        {month} {formatMessage({ id: 'month' })}
                    </MenuItem>
                ))}
            </Select>

            <Box sx={styles.weeks}>
                <Box
                    sx={styles.weekTab(!weekNumber)}
                    onClick={() => onChange(monthNumber, undefined)}
                >
                    {formatMessage({ id: 'allWeeks' })}
                </Box>

                {WEEKS.map((week) => (
                    <Box
                        key={week}
                        sx={styles.weekTab(week === weekNumber)}
                        onClick={() => onChange(monthNumber, week)}
                    >
                        {week} {formatMessage({ id: 'week' })}
                    </Box>
                ))}

                {autoEnabled && (
                    <Box
                        sx={{ ...styles.weekTab(!!curMonth && onToday), opacity: curMonth ? 1 : 0.5, pointerEvents: curMonth ? 'auto' : 'none' }}
                        title={formatMessage({ id: 'dirTodayHint' })}
                        onClick={() => onChange(curMonth, curWeek || undefined)}
                    >
                        ⏱ {formatMessage({ id: 'dirToday' })}
                    </Box>
                )}
            </Box>

            {autoEnabled && (
                <Box sx={{ fontSize: '12px', color: ui.muted }}>
                    {!courseId && !standalone
                        ? director ? formatMessage({ id: 'dirPickLekHint' }) : ''
                        : curMonth && onToday
                          ? formatMessage(
                                { id: 'dirByPeriod' },
                                { month: curMonth, week: curWeek, range: dateRange(current?.weekStart, current?.weekEnd) },
                            )
                          : ''}
                </Box>
            )}
        </Box>
    )
}

export default MonthWeekFilter
